import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '@/components/AuthLayout';
import { Button, Field, Input } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { ApiError, api } from '@/lib/api';

/* -------------------------------------------------------------------------
 * Setting a new password from an emailed link.
 *
 * ⚠️ TWO REFUSALS THAT MUST NEVER RENDER ALIKE.
 *
 *   410 Gone  the LINK is spent or expired. Nothing the reader types here can
 *             succeed, so the form is REPLACED by a route back to
 *             /forgot-password. Showing the form again would leave somebody
 *             retyping a password into a dead link for ever, each attempt
 *             failing for a reason the screen never gave them.
 *   422       the PASSWORD was refused. The link is fine; the field is not, so
 *             the message lands on the field and the form stays.
 *
 * A missing or empty `?token=` is a third state, and it fires no request: an
 * empty token cannot be the token that was emailed, so sending it would spend
 * a rate-limited call to be told something we already know. Say the link is
 * incomplete and offer the route that issues a whole one.
 *
 * Reached with no session, so nothing here may touch `useAuth()`.
 * --------------------------------------------------------------------- */

/*
 * Mirrored from `PASSWORD_MIN` / `PASSWORD_MAX` and the three validation
 * sentences in `src/components/users/UserFormModal.tsx`, which is where this
 * app already states the rule for the same column. They are module-private
 * there, so this is a second copy rather than an import — the wording is kept
 * byte-identical so a grep finds both, and the right fix is to lift them into
 * a shared module the day anything else needs them. Two rules for one column
 * is how they drift.
 *
 * 🔴 BOTH COPIES WENT 12 -> 8 ON 2026-09-04, owner's instruction. This is the
 * drift the paragraph above warns about, arriving for real: the change had to
 * be made twice, and a grep for `12` would have found neither, because every
 * sentence composes the number through a template literal.
 */
const PASSWORD_MIN = 8;
const PASSWORD_MAX = 200;

/** The one key this form renders a control for; see `routeFieldErrors`. */
const KNOWN_WRITE_KEYS = ['password'] as const;

// No icon library for three glyphs; paths are Lucide's, on a 24x24 stroked
// viewBox. Same set as LoginPage, which these pages sit beside.
const STROKE = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

function EyeIcon() {
  return (
    <svg {...STROKE} className="h-4 w-4" aria-hidden="true">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg {...STROKE} className="h-4 w-4" aria-hidden="true">
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <path d="m2 2 20 20" />
    </svg>
  );
}

// A neutral TextLink, not accent: navy is reserved for primary actions.
const BACK_LINK = 'font-medium';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  /*
   * No argument. The hook's first parameter is a *default init*, not options —
   * passing one injects its keys into the query string and then into whatever
   * is parsed back out of it.
   */
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') ?? '').trim();

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [clientError, setClientError] = useState<string | null>(null);
  /** The 410 branch: this link is spent. Distinct from an ordinary error. */
  const [linkDead, setLinkDead] = useState(false);
  const [done, setDone] = useState(false);

  const inFlight = useRef<AbortController | null>(null);
  useEffect(() => () => inFlight.current?.abort(), []);

  /*
   * Partitioned on this form's own write keys rather than on the shape of the
   * path: a 422 naming `token` is real and routes to no control here, so it
   * belongs in the banner. Left in `fieldErrors` it would vanish from the page
   * while still making the map non-empty.
   */
  const routed =
    error instanceof ApiError
      ? error.routeFieldErrors(KNOWN_WRITE_KEYS)
      : { fields: {} as Record<string, string>, unroutable: [] as string[] };

  const passwordError = clientError ?? routed.fields.password;
  const bannerLines =
    routed.unroutable.length > 0
      ? routed.unroutable
      : error && !(error instanceof ApiError && Object.keys(routed.fields).length > 0)
        ? [error.message]
        : [];

  function validate(): string | null {
    if (password === '') return `Choose a password of at least ${PASSWORD_MIN} characters.`;
    if (password.length < PASSWORD_MIN) {
      return `Password must be at least ${PASSWORD_MIN} characters — that one is ${password.length}.`;
    }
    if (password.length > PASSWORD_MAX) {
      return `Password must be ${PASSWORD_MAX} characters or fewer.`;
    }
    return null;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    // An empty token never reaches the wire — see the header.
    if (token === '') return;

    const invalid = validate();
    setClientError(invalid);
    if (invalid) return;

    const controller = new AbortController();
    inFlight.current = controller;
    const { signal } = controller;

    setError(null);
    setSubmitting(true);
    try {
      await api.resetPassword({ token, password }, signal);
      if (signal.aborted) return;
      setDone(true);
    } catch (caught) {
      if (signal.aborted) return;
      /*
       * The only branch on status in this file, and the reason the two states
       * exist: 410 is about the LINK and 422 is about the PASSWORD, and they
       * send the reader to different places.
       */
      if (caught instanceof ApiError && caught.status === 410) {
        setLinkDead(true);
        return;
      }
      setError(caught instanceof Error ? caught : new Error('Something went wrong'));
    } finally {
      if (!signal.aborted) setSubmitting(false);
    }
  }

  /* ---- The link never carried a token ------------------------------- */
  if (token === '') {
    return (
      <AuthLayout title="This reset link is incomplete">
        <div className="space-y-4">
          <p className="m-0 text-sm text-fg-secondary">
            This link carries no reset token. Request a new one and open it in full.
          </p>
          <Button variant="primary" className="w-full" onClick={() => navigate('/forgot-password')}>
            Request a new link
          </Button>
          <p className="m-0 text-center text-xs text-fg-subtle">
            <TextLink to="/login" className={BACK_LINK}>
              Back to sign in
            </TextLink>
          </p>
        </div>
      </AuthLayout>
    );
  }

  /* ---- 410: spent or expired ---------------------------------------- */
  if (linkDead) {
    return (
      <AuthLayout title="This reset link has expired">
        <div className="space-y-4">
          <p className="m-0 text-sm text-fg-secondary">
            A reset link works once. Request a new one and use the newest email.
          </p>
          <Button variant="primary" className="w-full" onClick={() => navigate('/forgot-password')}>
            Request a new link
          </Button>
          <p className="m-0 text-center text-xs text-fg-subtle">
            <TextLink to="/login" className={BACK_LINK}>
              Back to sign in
            </TextLink>
          </p>
        </div>
      </AuthLayout>
    );
  }

  /* ---- Done ---------------------------------------------------------- */
  if (done) {
    return (
      <AuthLayout title="Your password is set">
        <div className="space-y-4">
          <p className="m-0 text-sm text-fg-secondary">Sign in with your new password.</p>
          {/*
           * Not a tip: the server revokes every refresh token on a reset, so
           * the reader's other devices ARE signed out. Someone who does not
           * know that reads their phone dropping the session as a fault and
           * reports it — which is the reader concluding the app is broken when
           * it did exactly what they asked.
           */}
          <p className="m-0 text-sm text-fg-muted">
            Your other devices, phone included, have been signed out.
          </p>
          <Button variant="primary" className="w-full" onClick={() => navigate('/login')}>
            Go to sign in
          </Button>
        </div>
      </AuthLayout>
    );
  }

  /* ---- The form ------------------------------------------------------ */
  return (
    <AuthLayout title="Choose a new password">
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <Field label="New password" required error={passwordError}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                // Clear the refusal as soon as the value it described changes;
                // leaving it up marks a field the reader has already fixed.
                setClientError(null);
              }}
              maxLength={PASSWORD_MAX}
              // Never `current-password`: the old one is exactly what this
              // page cannot accept.
              autoComplete="new-password"
              required
              autoFocus
              invalid={Boolean(passwordError)}
              aria-describedby={describedBy}
              trailingSlot={
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  leadingIcon={showPassword ? <EyeOffIcon /> : <EyeIcon />}
                />
              }
            />
          )}
        </Field>

        {bannerLines.length > 0 && (
          <Notice tone="danger" role="alert" className="break-words">
            <span className="block space-y-1">
              {bannerLines.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </span>
          </Notice>
        )}

        <Button
          type="submit"
          variant="primary"
          loading={submitting}
          disabled={submitting}
          className="w-full"
        >
          Set password
        </Button>

        <p className="m-0 text-center text-xs text-fg-subtle">
          <TextLink to="/login" className={BACK_LINK}>
            Back to sign in
          </TextLink>
        </p>
      </form>
    </AuthLayout>
  );
}
